import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, ArrowLeft, AlertCircle, Newspaper } from "lucide-react";
import Navbar from "../../../../components/navbar/Navbar";
import Footer from "../../../../components/footer/Footer";
import { currentAffairsApi } from "../../../../api/api";
import api from "../../../../utils/api";

const CurrentAffairDetails = () => {
    const [blogsDetails, setBlogsDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCourseDetails = async () => {
            try {
                const response = await api.get(`${currentAffairsApi}/${id}`);
                setBlogsDetails(response.data);
                setLoading(false);
            } catch (err) {
                setError("Current Affairs not found!");
                setLoading(false);
            }
        };

        fetchCourseDetails();
    }, [id]);

    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.style.objectFit = "contain";
        e.target.style.backgroundColor = "#f0f0f0";
        e.target.style.filter = "grayscale(100%)";
        e.target.style.opacity = "0.5";
        e.target.src = "/assets/logo.png";
    };

    // Animation variants matching the established theme
    const fadeUpVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" },
        },
    };

    if (loading) {
        return (
            <div className="bg-white min-h-screen flex flex-col">
                
                <main className="flex-grow pt-32 pb-24 px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-4xl mx-auto animate-pulse">
                        <div className="w-24 h-6 bg-gray-100 rounded mb-8"></div>
                        <div className="w-full h-12 bg-gray-200 rounded mb-4"></div>
                        <div className="w-3/4 h-12 bg-gray-200 rounded mb-8"></div>
                        <div className="flex gap-4 mb-12">
                            <div className="w-32 h-6 bg-gray-100 rounded"></div>
                        </div>
                        <div className="w-full aspect-video bg-gray-200 rounded-xl mb-12"></div>
                        <div className="space-y-4">
                            <div className="w-full h-4 bg-gray-100 rounded"></div>
                            <div className="w-full h-4 bg-gray-100 rounded"></div>
                            <div className="w-5/6 h-4 bg-gray-100 rounded"></div>
                        </div>
                    </div>
                </main>
                
            </div>
        );
    }

    if (error || !blogsDetails) {
        return (
            <div className="bg-gray-50 min-h-screen flex flex-col">
                
                <main className="flex-grow pt-32 pb-24 px-4 flex items-center justify-center">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeUpVariant}
                        className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center max-w-lg w-full"
                    >
                        <div className="w-16 h-16 bg-red-50 border border-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertCircle size={32} />
                        </div>
                        <h2 className="text-2xl font-black text-neutral-900 mb-4">
                            {error || "Article Not Found"}
                        </h2>
                        <p className="text-gray-500 font-medium mb-8">
                            The current affairs update you are looking for does
                            not exist or has been removed.
                        </p>
                        <button
                            className="inline-flex items-center px-6 py-3 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-lg transition-colors duration-300"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft size={18} className="mr-2" /> Go Back
                        </button>
                    </motion.div>
                </main>
                
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen flex flex-col">
            

            <main className="flex-grow pt-10 pb-24 px-4 sm:px-6 lg:px-8 w-full">
                <article className="container mx-auto">
                    {/* Back Navigation */}
                    <motion.button
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-sm font-bold text-gray-500 hover:text-neutral-900 transition-colors mb-10 group"
                    >
                        <ArrowLeft
                            size={16}
                            className="mr-2 transform group-hover:-translate-x-1 transition-transform"
                        />
                        Back to Updates
                    </motion.button>

                    {/* Article Header */}
                    <motion.header
                        initial="hidden"
                        animate="visible"
                        variants={fadeUpVariant}
                        className="mb-12"
                    >
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-5 tracking-widest">
                            <span className="w-6 h-1 bg-red-700"></span>
                            <Newspaper size={14} className="text-red-700" />
                            Current Affairs Update
                        </div>

                        <h1 className="text-4xl md:text-5xl lg:text-6xl text-neutral-900 mb-8 leading-tight font-bold">
                            {blogsDetails.title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-6 py-4 border-y border-gray-100">
                            <div className="flex items-center text-gray-500 font-bold text-sm uppercase tracking-widest">
                                <CalendarDays
                                    size={16}
                                    className="mr-2 text-yellow-500"
                                />
                                {new Intl.DateTimeFormat("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                }).format(new Date(blogsDetails.created_at))}
                            </div>
                        </div>
                    </motion.header>

                    {/* Lead Paragraph (Short Description) */}
                    {blogsDetails.short_description && (
                        <motion.div
                            initial="hidden"
                            animate="visible"
                            variants={fadeUpVariant}
                            className="prose prose-lg max-w-none text-gray-600 font-medium leading-relaxed border-l-4 border-yellow-400 pl-6 mb-12"
                            dangerouslySetInnerHTML={{
                                __html: blogsDetails.short_description,
                            }}
                        />
                    )}

                    {/* Main Content */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUpVariant}
                        className="prose prose-lg md:prose-xl max-w-none text-neutral-800 prose-headings:font-black prose-headings:text-neutral-900 prose-a:text-red-700 prose-a:font-bold hover:prose-a:text-neutral-900 prose-img:rounded-xl prose-img:border prose-img:border-gray-200 leading-relaxed"
                        style={{ whiteSpace: "pre-wrap" }}
                        dangerouslySetInnerHTML={{
                            __html: blogsDetails.description,
                        }}
                    />
                </article>
            </main>

            
        </div>
    );
};

export default CurrentAffairDetails;
